import FuelRouteApp from './components/FuelRouteApp';
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import Login from './components/Login';
import HowItWorks from './components/HowItWorks';
import Subscription from './components/Subscription';
import Support from './components/Support';
import Help from './components/Help';
import './App.css';
import { useLocation } from 'react-router-dom';
import { trackPageview } from './analytics';
import apiService from './services/api';

// Tracks a Google Analytics page_view every time the route changes
function AnalyticsListener() {
  const location = useLocation();

  useEffect(() => {
    trackPageview(location.pathname + location.search);
  }, [location]);

  return null; // invisible helper
}

// API Service - Backend Integration (Production level)
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://fuelroutepro-thampico.onrender.com/api';
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://fuelroute-backend-295738019.us-central1.run.app/api';

const makeRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  console.log('API URL:', url);
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, { ...defaultOptions, ...options });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// API functions
const checkApiHealth = () => makeRequest('/health');
const getFuelTypes = () => makeRequest('/fuel-types');
const getRouteHistory = () => makeRequest('/routes');
const calculateRouteCost = (routeData) => makeRequest('/calculate-cost', {
  method: 'POST',
  body: JSON.stringify(routeData),
});

function App() {
  // Backend integration state
  const [apiStatus, setApiStatus] = useState('checking');
  const [fuelTypes, setFuelTypes] = useState(['hydrogen', 'methanol', 'ammonia']);
  const [routeHistory, setRouteHistory] = useState([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportEmail, setReportEmail] = useState('');
  const [isSendingReport, setIsSendingReport] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [latestReportData, setLatestReportData] = useState(null);

  const openReportModal = (e) => {
    e.preventDefault();
    setReportError(null);
    setReportSuccess(false);
    setShowReportModal(true);
  };

  const closeReportModal = () => {
    setShowReportModal(false);
    setReportEmail('');
    setIsSendingReport(false);
  };

  // const saveFile = async (blob, suggestedName) => {
  //   // Feature detection for the File System Access API
  //   console.log('File System Access API supported:', 'showSaveFilePicker' in window);
  //   if ('showSaveFilePicker' in window && window.self === window.top) {
  //     console.log('File System Access API supported: true');
  //     console.log(`file name: ${suggestedName}`);
  //     try {
  //       // Show the file save dialog
  //       const handle = await window.showSaveFilePicker({
  //         suggestedName,
  //         types: [{
  //           description: 'Text file',
  //           accept: {
  //             'text/plain': ['.txt'],
  //           },
  //         }, {
  //           description: 'PDF file',
  //           accept: {
  //             'application/pdf': ['.pdf'],
  //           },
  //         }],
  //       });

  //       // Write the blob to the file
  //       const writable = await handle.createWritable();
  //       await writable.write(blob);
  //       await writable.close();
  //       console.log('File saved successfully');
  //     } catch (err) {
  //       console.error('Error saving file:', err);
  //     }
  //   } else {
  //     // Fallback for browsers that do not support the API
  //     console.log('File System Access API not supported, using fallback method.');
  //     // Call the traditional download fallback
  //     window.downloadFileFallback(blob, suggestedName);
  //   }
  // };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    if (!reportEmail) return;

    if (!latestReportData) {
      setReportError('Please run "Calculate with AI Enhancement" first so we can include your route details.');
      return;
    }

    setIsSendingReport(true);
    setReportError(null);
    setReportSuccess(false);

    try {
      const report = await apiService.sendReportRequest(reportEmail, latestReportData);
      setReportSuccess(true);

      if (report.report_html) {
        console.log('Report HTML:', report.report_html);
      }

      // Automatically close the modal after 2 seconds to allow user to see success message
      setTimeout(() => {
        closeReportModal();
      }, 2000);
    } catch (error) {
      console.error('Report request failed:', error);
      setReportError('Could not send the report request. Please try again.');
    } finally {
      setIsSendingReport(false);
    }
  };

  useEffect(() => {
    // Backend initialization
    const initializeBackend = async () => {
      console.log('🚀 Initializing backend...');
      try {
        // Check API health
        await checkApiHealth();
        setApiStatus('connected');
        console.log('✅ Backend connected successfully');

        // Load fuel types
        try {
          const fuelTypesResponse = await getFuelTypes();
          setFuelTypes(fuelTypesResponse.data || ['hydrogen', 'methanol', 'ammonia']);
        } catch (error) {
          console.warn('Using default fuel types:', error.message);
        }

        // Load route history
        try {
          const historyResponse = await getRouteHistory();
          setRouteHistory(historyResponse.data || []);
        } catch (error) {
          console.warn('Could not load route history:', error.message);
        }

      } catch (error) {
        console.error('Backend connection failed:', error);
        setApiStatus('error');
      }
    };

    // Smooth scrolling for navigation links
    const handleSmoothScroll = (e) => {
      if (e.target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault();
        const target = document.querySelector(e.target.getAttribute('href'));
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    };

    // Hide header-top on scroll down
    let lastScrollY = window.scrollY;
    const headerTop = document.querySelector('.header-top');
    const header = document.querySelector('.header');

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        if (headerTop) headerTop.style.transform = 'translateY(-100%)';
        if (header) header.style.top = '0';
      } else {
        if (headerTop) headerTop.style.transform = 'translateY(0)';
        if (header) header.style.top = '40px';
      }

      lastScrollY = currentScrollY;
    };

    // Initialize everything
    console.log('🚀 Initializing app...');
    initializeBackend();
    document.addEventListener('click', handleSmoothScroll);
    window.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('click', handleSmoothScroll);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Backend integration functions to pass to FuelRouteApp
  const backendAPI = {
    calculateCost: calculateRouteCost,
    isConnected: apiStatus === 'connected',
    fuelTypes,
    routeHistory,
    refreshHistory: async () => {
      try {
        const historyResponse = await getRouteHistory();
        setRouteHistory(historyResponse.data || []);
      } catch (error) {
        console.warn('Could not refresh route history:', error.message);
      }
    }
  };

  return (
    <Router>
      <AnalyticsListener />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/support" element={<Support />} />
        <Route path="/help" element={<Help />} />
        <Route path="/subscribe" element={<Subscription />} />
        <Route path="/" element={
          <div className="App">

            {showReportModal && (
              <div className="report-modal-overlay" onClick={closeReportModal}>
                <div className="report-modal" onClick={(e) => e.stopPropagation()}>
                  <h3>Generate Report</h3>
                  <p className="report-modal-description">
                    Enter the email address where you want to receive the report.
                  </p>
                  <form className="report-modal-form">
                    <label htmlFor="report-email" className="report-modal-label">Email address</label>
                    <input
                      id="report-email"
                      type="email"
                      value={reportEmail}
                      onChange={(e) => setReportEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                    />
                    {reportError && <div className="report-modal-error">{reportError}</div>}
                    {reportSuccess && <div className="report-modal-success">Report request sent! Check your inbox soon.</div>}
                    <div className="report-modal-actions">
                      <button type="button" className="report-modal-button secondary" onClick={closeReportModal}>
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="report-modal-button"
                        disabled={isSendingReport || !reportEmail}
                        onClick={handleReportSubmit}
                      >
                        {isSendingReport ? 'Sending...' : 'Send Report'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Header Top */}
            <div className="header-top">
              <div className="header-top-container">
                <Link to="/how-it-works">📖 How It Works</Link>
                <Link to="/help">❓ Help</Link>
                <Link to="/support">📞 Support</Link>
                <span className={`backend-status ${apiStatus}`}>
                  {apiStatus === 'connected' ? '🟢 Backend Online (AI Enhanced)' :
                    apiStatus === 'error' ? '🔴 Backend Offline' :
                      '🟡 Connecting...'}
                </span>
              </div>
            </div>

            {/* Header */}
            <header className="header">
              <div className="nav-container">
                <div className="logo">
                  FuelRoute Pro
                  <div className="logo-subtitle">By THAMPICO</div>
                </div>

                <div className="header-right">
                  <div className="search-icon"></div>
                  <a href="fuelroute-documentation.html" target="_blank" rel="noopener noreferrer" className="documentation-button">
                    Documentation
                  </a>
                  <a href="#calculator" className="cta-button">Try Calculator</a>
                  <button type="button" className="cta-button" onClick={openReportModal}>Generate Report</button>
                </div>
              </div>
            </header>

            {/* Hero Section */}
            <section className="hero">
              <div className="hero-container">
                <div className="hero-content">
                  <h1>The Future of Fuel Transportation</h1>
                  <p className="subtitle">AI-powered cost estimation for hydrogen, methanol, and ammonia logistics across all transport modes</p>
                  <p className="description">
                    Calculate transportation costs for hydrogen, methanol, and ammonia across multiple modes. Optimize routes, reduce costs, and accelerate the clean energy transition.
                  </p>

                  <p className="description">
                    DISCLAIMER: FuelRoute Pro provides estimates for planning purposes only. Results are not guaranteed and should not be used for financial or business decisions. THAMPICO LLC makes no warranties and accepts no liability for use of this tool.
                  </p>


                  <div className="hero-stats">
                    <div className="stat-item">
                      <div className="stat-number">3</div>
                      <div className="stat-label">Alternative Fuels</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-number">2</div>
                      <div className="stat-label">Transport Modes</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-number">AI</div>
                      <div className="stat-label">Powered Analysis</div>
                    </div>
                  </div>
                </div>

                <div className="hero-content">

                  <div className="hero-visual">
                    <div className="fuel-cards">
                      <div className="fuel-card">
                        <div className="fuel-formula">H₂</div>
                        <div className="fuel-name">Hydrogen</div>
                      </div>
                      <div className="fuel-card">
                        <div className="fuel-formula">CH₃OH</div>
                        <div className="fuel-name">Methanol</div>
                      </div>
                      <div className="fuel-card">
                        <div className="fuel-formula">NH₃</div>
                        <div className="fuel-name">Ammonia</div>
                      </div>
                    </div>
                  </div>

                  <div className="hero-iframe">
                    <iframe width="480" height="270" src="https://www.youtube.com/embed/WRPWeBy7fK8?si=TyiTzZ4sTrwz_yCA" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
                  </div>

                </div>
              </div>
            </section>







            {/* Calculator Section */}
            <section id="calculator">
              <FuelRouteApp
                backendAPI={backendAPI}
                apiStatus={apiStatus}
                onReportDataReady={setLatestReportData}
              />
            </section>

            {/* Footer */}
            <footer className="footer">
              <div className="footer-container">
                <div className="footer-brand">

                  <div className="social-links">
                    <a href="mailto:info@thampico.com" title="Email">Email</a>
                    <a href="https://www.linkedin.com/company/thampico/" target="_blank" rel="noopener noreferrer" title="LinkedIn">LinkedIn</a>

                  </div>
                </div>

                <div className="footer-section">

                </div>

              </div>

              <div className="footer-bottom">
                <div>© 2025 FuelRoute Pro. All Rights Reserved. Developed by THAMPICO Capstone Program.</div>
                <div className="footer-bottom-links">
                  <a href="#terms">Terms & Conditions</a>
                  <a href="#privacy">Privacy Policy</a>
                  <a href="#cookies">Cookie Policy</a>
                </div>
              </div>
            </footer>
          </div>
        } />
      </Routes>
    </Router>
  );
}

export default App;
